import React, { useEffect, useMemo, useState } from 'react'
import { ethers } from 'ethers'

import { uploadMarketplaceMetadata } from './marketplaceData'
import { getSettlementMode, isSettlementTokenReady } from './marketplaceAdminModel'
import { migrationErrorKey } from './marketplaceMigration'
import { shortAddress } from './marketplaceModel'
import MarketplaceImageUploader from './MarketplaceImageUploader'
import { AddressValue, EmptyState, MarketDialog } from './MarketplaceParts'

const ZERO_ADDRESS = ethers.constants.AddressZero

function errorKey(error) {
    if (String(error?.code || '').startsWith('migration')) return error.code
    const decodedMigrationError = migrationErrorKey(error)
    if (decodedMigrationError) return decodedMigrationError
    const message = error?.error?.message || error?.data?.message || error?.reason || error?.message || String(error)
    if (message === 'walletRequired') return 'walletRequired'
    if (message === 'networkRequired') return 'networkRequired'
    if (message === 'invalidAddress') return 'invalidAddress'
    return message
}

function SettingCard({ icon, title, value, description, detail, onEdit, t, tone = '' }) {
    return <article className={`market-admin-setting-card ${tone ? `is-${tone}` : ''}`}>
        <div className="market-admin-setting-card__top">
            <span className="market-admin-setting-card__icon"><i className={`fa ${icon}`} /></span>
            <span className="market-admin-setting-card__heading"><small>{title}</small><strong>{value}</strong></span>
            <button type="button" onClick={onEdit} title={t('editSetting')}><i className="fa fa-pen" /></button>
        </div>
        <p>{description}</p>
        {detail && <div className="market-admin-setting-card__detail">{detail}</div>}
    </article>
}

function DialogFooter({ busy, disabled, onClose, onSave, t, saveLabel = 'saveChanges' }) {
    return <>
        <button type="button" className="market-button" disabled={busy} onClick={onClose}>{t('cancel')}</button>
        <button type="button" className="market-button is-primary" disabled={busy || disabled} onClick={onSave}>
            {busy && <i className="fa fa-spinner fa-spin" />} {t(saveLabel)}
        </button>
    </>
}

function SettlementEditor({ market, t, onClose }) {
    const initialMode = getSettlementMode(market.core.settlementAddress)
    const [mode, setMode] = useState(initialMode)
    const [address, setAddress] = useState(initialMode === 'erc20' ? market.core.settlementAddress : '')
    const [token, setToken] = useState(initialMode === 'erc20' ? market.core.currency : null)
    const [checking, setChecking] = useState(false)
    const [tokenError, setTokenError] = useState('')
    const [busy, setBusy] = useState(false)
    const tokenReady = isSettlementTokenReady({ mode, address, token })

    useEffect(() => {
        if (mode !== 'erc20' || !ethers.utils.isAddress(address) || address === ZERO_ADDRESS) {
            setToken(null)
            setTokenError('')
            return undefined
        }
        let active = true
        const timer = window.setTimeout(async () => {
            setChecking(true)
            setTokenError('')
            try {
                const next = await market.inspectSettlementToken(address)
                if (active) setToken(next)
            } catch (error) {
                if (active) {
                    setToken(null)
                    setTokenError(t('tokenReadFailed'))
                }
            } finally {
                if (active) setChecking(false)
            }
        }, 350)
        return () => {
            active = false
            window.clearTimeout(timer)
        }
    }, [address, market, mode, t])

    const save = async () => {
        setBusy(true)
        try {
            await market.saveSettlement(mode === 'native' ? ZERO_ADDRESS : token.address)
            onClose()
        } catch (error) {
            market.showToast(errorKey(error), 'error')
        } finally {
            setBusy(false)
        }
    }

    return <MarketDialog
        title={t('defaultSettlement')}
        eyebrow={t('settlementEditorHint')}
        icon="fa-coins"
        onClose={onClose}
        footer={<DialogFooter busy={busy} disabled={!tokenReady} onClose={onClose} onSave={save} t={t} saveLabel="saveSettlement" />}
    >
        <div className="market-segments market-admin-asset-mode">
            <button type="button" className={mode === 'native' ? 'is-active' : ''} onClick={() => setMode('native')}><i className="fa fa-gem" /> {t('nativeCurrency')}</button>
            <button type="button" className={mode === 'erc20' ? 'is-active' : ''} onClick={() => setMode('erc20')}><i className="fa fa-coins" /> {t('erc20Token')}</button>
        </div>
        {mode === 'native' ? <div className="market-admin-native-preview">
            <span><i className="fa fa-gem" /></span>
            <div><small>{t('selectedSettlement')}</small><strong>{market.network.nativeCurrency.name} ({market.network.nativeCurrency.symbol})</strong><p>{t('nativeSettlementHint')}</p></div>
        </div> : <>
            <label className="market-field"><span>{t('tokenContract')}</span><input value={address} onChange={(event) => setAddress(event.target.value.trim())} placeholder="0x..." autoFocus /></label>
            <div className={`market-token-preview ${checking ? 'is-loading' : ''} ${tokenError ? 'is-error' : ''}`}>
                {checking ? <><i className="fa fa-circle-notch fa-spin" /><span>{t('readingToken')}</span></>
                    : token ? <><span className="market-token-preview__symbol">{token.symbol?.slice(0, 3)}</span><div><small>{t('tokenDetected')}</small><strong>{token.name} ({token.symbol})</strong><p>{t('tokenDecimals', { decimals: token.decimals })}</p></div><i className="fa fa-check-circle" /></>
                        : <><i className={`fa ${tokenError ? 'fa-exclamation-circle' : 'fa-search'}`} /><span>{tokenError || t('enterTokenAddress')}</span></>}
            </div>
        </>}
    </MarketDialog>
}

function ProjectEditor({ market, t, onClose }) {
    const [values, setValues] = useState({
        name: market.project.name || market.core.nftName,
        logo: market.project.logo || market.project.image || '',
        description: market.project.description || '',
        website: market.project.website || '',
        twitter: market.project.twitter || '',
        telegram: market.project.telegram || '',
        youtube: market.project.youtube || '',
    })
    const [busy, setBusy] = useState(false)
    const [logoUploading, setLogoUploading] = useState(false)
    const update = (key, value) => setValues((current) => ({ ...current, [key]: value }))
    const save = async () => {
        setBusy(true)
        try {
            const metadata = { ...market.project, ...values, nft: market.core.nftAddress, schemaVersion: 1 }
            delete metadata.extra
            const metadataURI = await uploadMarketplaceMetadata(metadata)
            await market.runAdmin('setMetadataURI', [metadataURI])
            onClose()
        } catch (error) {
            market.showToast(errorKey(error), 'error')
        } finally {
            setBusy(false)
        }
    }
    return <MarketDialog title={t('projectMetadata')} eyebrow={t('projectEditorHint')} icon="fa-image" onClose={onClose} wide footer={<DialogFooter busy={busy || logoUploading} disabled={!values.name.trim()} onClose={onClose} onSave={save} t={t} />}>
        <MarketplaceImageUploader
            value={values.logo}
            onChange={(logo) => update('logo', logo)}
            onUploadingChange={setLogoUploading}
            t={t}
        />
        <div className="market-form-grid">
            <label className="market-field"><span>{t('projectName')}</span><input value={values.name} onChange={(event) => update('name', event.target.value)} /></label>
            <label className="market-field"><span>{t('website')}</span><input value={values.website} onChange={(event) => update('website', event.target.value)} /></label>
        </div>
        <div className="market-form-grid is-links">{['twitter', 'telegram', 'youtube'].map((key) => <label className="market-field" key={key}><span>{t(key)}</span><input value={values[key]} onChange={(event) => update(key, event.target.value)} /></label>)}</div>
        <label className="market-field"><span>{t('projectDescription')}</span><textarea rows="5" maxLength="3000" value={values.description} onChange={(event) => update('description', event.target.value)} /></label>
    </MarketDialog>
}

function ProjectFeeEditor({ market, t, onClose }) {
    const [percent, setPercent] = useState(String(market.core.projectFeeBps / 100))
    const [recipient, setRecipient] = useState(market.core.projectFeeRecipient)
    const [busy, setBusy] = useState(false)
    const save = async () => {
        setBusy(true)
        try {
            await market.saveProjectFee(percent, recipient)
            onClose()
        } catch (error) {
            market.showToast(errorKey(error), 'error')
        } finally {
            setBusy(false)
        }
    }
    return <MarketDialog title={t('projectFee')} eyebrow={t('feeEditorHint')} icon="fa-percentage" onClose={onClose} footer={<DialogFooter busy={busy} disabled={!recipient || Number(percent) < 0 || Number(percent) > 20} onClose={onClose} onSave={save} t={t} saveLabel="saveProject" />}>
        <div className="market-admin-callout"><i className="fa fa-lock" /><span>{t('snapshotHint')}</span></div>
        <label className="market-field"><span>{t('feePercent')}</span><div className="market-price-input"><input type="number" min="0" max="20" step="0.01" value={percent} onChange={(event) => setPercent(event.target.value)} /><strong>%</strong></div></label>
        <label className="market-field"><span>{t('feeRecipient')}</span><input value={recipient} onChange={(event) => setRecipient(event.target.value.trim())} /></label>
    </MarketDialog>
}

function RoyaltyEditor({ market, t, onClose }) {
    const [mode, setMode] = useState(String(market.core.royaltyMode))
    const [percent, setPercent] = useState(String(market.core.customRoyaltyBps / 100))
    const [recipient, setRecipient] = useState(market.core.customRoyaltyRecipient === ZERO_ADDRESS ? '' : market.core.customRoyaltyRecipient)
    const [busy, setBusy] = useState(false)
    const save = async () => {
        setBusy(true)
        try {
            await market.saveRoyalty(mode, percent, recipient)
            onClose()
        } catch (error) {
            market.showToast(errorKey(error), 'error')
        } finally {
            setBusy(false)
        }
    }
    return <MarketDialog title={t('royaltySettings')} eyebrow={t('royaltyEditorHint')} icon="fa-certificate" onClose={onClose} footer={<DialogFooter busy={busy} disabled={mode === '2' && (!recipient || Number(percent) < 0 || Number(percent) > 15)} onClose={onClose} onSave={save} t={t} saveLabel="saveRoyalty" />}>
        <div className="market-segments"><button type="button" className={mode === '0' ? 'is-active' : ''} onClick={() => setMode('0')}>{t('royaltyNone')}</button><button type="button" className={mode === '1' ? 'is-active' : ''} onClick={() => setMode('1')}>{t('royaltyErc2981')}</button><button type="button" className={mode === '2' ? 'is-active' : ''} onClick={() => setMode('2')}>{t('royaltyCustom')}</button></div>
        {mode === '2' && <><label className="market-field"><span>{t('royaltyPercent')}</span><div className="market-price-input"><input type="number" min="0" max="15" step="0.01" value={percent} onChange={(event) => setPercent(event.target.value)} /><strong>%</strong></div></label><label className="market-field"><span>{t('royaltyRecipient')}</span><input value={recipient} onChange={(event) => setRecipient(event.target.value.trim())} /></label></>}
        <div className="market-admin-callout"><i className="fa fa-lock" /><span>{t('snapshotHint')}</span></div>
    </MarketDialog>
}

function MarketStateEditor({ market, t, onClose }) {
    const [busy, setBusy] = useState(false)
    const toggle = async () => {
        setBusy(true)
        try {
            await market.runAdmin('setPaused', [!market.core.paused])
            onClose()
        } catch (error) {
            market.showToast(errorKey(error), 'error')
        } finally {
            setBusy(false)
        }
    }
    return <MarketDialog title={t('marketControls')} eyebrow={t('marketStateEditorHint')} icon="fa-shield-alt" onClose={onClose} footer={<><button type="button" className="market-button" onClick={onClose}>{t('cancel')}</button><button type="button" className={`market-button ${market.core.paused ? 'is-primary' : 'is-danger'}`} disabled={busy || market.core.retired} onClick={toggle}>{busy && <i className="fa fa-spinner fa-spin" />} <i className={`fa fa-${market.core.paused ? 'play' : 'pause'}`} /> {market.core.paused ? t('resumeMarket') : t('pauseMarket')}</button></>}>
        <div className={`market-admin-state-preview ${market.core.paused ? 'is-paused' : 'is-live'}`}><i className={`fa fa-${market.core.paused ? 'pause-circle' : 'check-circle'}`} /><div><small>{t('currentStatus')}</small><strong>{market.core.paused ? t('paused') : t('trading')}</strong><p>{market.core.paused ? t('pausedHint') : t('tradingHint')}</p></div></div>
    </MarketDialog>
}

function OwnershipEditor({ market, t, onClose }) {
    const [newOwner, setNewOwner] = useState('')
    const [busy, setBusy] = useState(false)
    const save = async () => {
        setBusy(true)
        try {
            await market.runAdmin('transferOwnership', [newOwner])
            onClose()
        } catch (error) {
            market.showToast(errorKey(error), 'error')
        } finally {
            setBusy(false)
        }
    }
    return <MarketDialog title={t('transferOwner')} eyebrow={t('ownershipEditorHint')} icon="fa-user-shield" onClose={onClose} footer={<DialogFooter busy={busy} disabled={!ethers.utils.isAddress(newOwner)} onClose={onClose} onSave={save} t={t} saveLabel="transferOwner" />}>
        <div className="market-admin-current-value"><small>{t('currentOwner')}</small><AddressValue value={market.core.owner} copyValue={market.copyValue} label={t('copy')} /></div>
        <label className="market-field"><span>{t('newOwner')}</span><input value={newOwner} onChange={(event) => setNewOwner(event.target.value.trim())} placeholder="0x..." /></label>
        <div className="market-admin-callout is-warning"><i className="fa fa-exclamation-triangle" /><span>{t('ownershipWarning')}</span></div>
    </MarketDialog>
}

function MigrationEditor({ market, t, onClose }) {
    const [target, setTarget] = useState(market.core.migrationTarget === ZERO_ADDRESS ? '' : market.core.migrationTarget)
    const [busy, setBusy] = useState('')
    const [validationError, setValidationError] = useState(null)
    const execute = async (key, method, args) => {
        setBusy(key)
        try {
            await market.runAdmin(method, args)
            if (method !== 'migrateBatch') onClose()
        } catch (error) {
            market.showToast(errorKey(error), 'error')
        } finally {
            setBusy('')
        }
    }
    const beginMigration = async () => {
        setBusy('start')
        setValidationError(null)
        try {
            await market.inspectMigrationTarget(target)
            await market.runAdmin('beginMigration', [target])
            onClose()
        } catch (error) {
            const key = errorKey(error)
            const values = error?.values || {}
            setValidationError({ key, values })
            market.showToast(key, 'error', { values })
        } finally {
            setBusy('')
        }
    }
    const footer = !market.core.migrationActive
        ? <DialogFooter busy={Boolean(busy)} disabled={!ethers.utils.isAddress(target)} onClose={onClose} onSave={beginMigration} t={t} saveLabel="beginMigration" />
        : <><button type="button" className="market-button" onClick={onClose}>{t('close')}</button>{!market.core.migrationStarted && <button type="button" className="market-button is-danger" disabled={busy} onClick={() => execute('cancel', 'cancelMigration', [])}>{t('cancelMigration')}</button>}<button type="button" className="market-button is-primary" disabled={busy} onClick={() => execute('next', 'migrateBatch', [20])}>{t('continueMigration')}</button></>
    return <MarketDialog title={t('migration')} eyebrow={t('migrationEditorHint')} icon="fa-truck-loading" onClose={onClose} footer={footer}>
        {market.core.migrationActive ? <div className="market-admin-state-preview is-migrating"><i className="fa fa-truck-loading" /><div><small>{t('migrationActive')}</small><strong>{t('migrationCursorValue', { cursor: market.core.migrationCursor.toString() })}</strong><AddressValue value={market.core.migrationTarget} copyValue={market.copyValue} label={t('copy')} /></div></div>
            : <><label className="market-field"><span>{t('migrationTarget')}</span><input value={target} onChange={(event) => { setTarget(event.target.value.trim()); setValidationError(null) }} placeholder="0x..." /></label>{validationError && <div className="market-admin-callout is-error"><i className="fa fa-times-circle" /><span>{t(validationError.key, validationError.values)}</span></div>}<div className="market-admin-callout is-warning"><i className="fa fa-exclamation-triangle" /><span>{t('migrationWarning')}</span></div></>}
    </MarketDialog>
}

export function AdminTab({ market, t }) {
    const { core } = market
    const [editor, setEditor] = useState('')
    const [busy, setBusy] = useState('')
    const isOwner = market.wallet.account && market.wallet.account.toLowerCase() === core.owner.toLowerCase()
    const isPendingOwner = market.wallet.account && market.wallet.account.toLowerCase() === core.pendingOwner.toLowerCase()
    const royaltyLabel = useMemo(() => {
        if (core.royaltyMode === 1) return t('royaltyErc2981')
        if (core.royaltyMode === 2) return `${core.customRoyaltyBps / 100}%`
        return t('royaltyNone')
    }, [core.customRoyaltyBps, core.royaltyMode, t])

    const acceptOwnership = async () => {
        setBusy('acceptOwner')
        try {
            await market.runAdmin('acceptOwnership', [])
        } catch (error) {
            market.showToast(errorKey(error), 'error')
        } finally {
            setBusy('')
        }
    }

    if (!isOwner && isPendingOwner) return <div className="market-admin"><div className="market-admin__notice"><i className="fa fa-key" /><span><strong>{t('pendingOwnership')}</strong><AddressValue value={core.marketAddress} copyValue={market.copyValue} label={t('copy')} /></span><button type="button" className="market-button is-primary" disabled={busy} onClick={acceptOwnership}>{busy && <i className="fa fa-spinner fa-spin" />} {t('acceptOwnership')}</button></div></div>
    if (!isOwner) return <EmptyState icon="fa-user-shield" title={t('ownerOnly')} text={shortAddress(core.owner)} />

    const editors = {
        settlement: SettlementEditor,
        project: ProjectEditor,
        projectFee: ProjectFeeEditor,
        royalty: RoyaltyEditor,
        state: MarketStateEditor,
        ownership: OwnershipEditor,
        migration: MigrationEditor,
    }
    const Editor = editors[editor]

    return <div className="market-admin">
        <header className="market-admin-settings__header">
            <div><span>{t('adminEyebrow')}</span><h2>{t('adminTitle')}</h2><p>{t('adminHint')}</p></div>
            <div className="market-admin-settings__identity"><small>{t('marketOwner')}</small><AddressValue value={core.owner} copyValue={market.copyValue} label={t('copy')} /></div>
        </header>
        <div className="market-admin-settings">
            <SettingCard icon="fa-coins" title={t('defaultSettlement')} value={`${core.currency.name} (${core.currency.symbol})`} description={core.currency.native ? t('nativeSettlementHint') : t('erc20SettlementHint')} detail={<span className="market-admin-chip">{core.currency.native ? t('nativeCurrency') : 'ERC-20'}</span>} onEdit={() => setEditor('settlement')} t={t} />
            <SettingCard icon="fa-image" title={t('projectMetadata')} value={market.project.name || core.nftName} description={market.project.description || t('projectEditorHint')} detail={<span>{[market.project.website && t('website'), market.project.twitter && 'Twitter', market.project.telegram && 'Telegram'].filter(Boolean).join(' · ') || t('noProjectLinks')}</span>} onEdit={() => setEditor('project')} t={t} />
            <SettingCard icon="fa-percentage" title={t('projectFee')} value={`${core.projectFeeBps / 100}%`} description={t('feeCardHint')} detail={<AddressValue value={core.projectFeeRecipient} copyValue={market.copyValue} label={t('copy')} />} onEdit={() => setEditor('projectFee')} t={t} />
            <SettingCard icon="fa-certificate" title={t('royaltySettings')} value={royaltyLabel} description={t('royaltyCardHint')} detail={core.royaltyMode === 2 ? <AddressValue value={core.customRoyaltyRecipient} copyValue={market.copyValue} label={t('copy')} /> : <span>{t(core.royaltyMode === 1 ? 'automaticRoyalty' : 'noRoyalty')}</span>} onEdit={() => setEditor('royalty')} t={t} />
            <SettingCard icon="fa-shield-alt" title={t('marketControls')} value={core.retired ? t('retired') : core.paused ? t('paused') : t('trading')} description={core.paused ? t('pausedHint') : t('tradingHint')} detail={<span className={`market-admin-chip ${core.paused ? 'is-warning' : 'is-live'}`}><i className="fa fa-circle" /> {core.paused ? t('paused') : t('trading')}</span>} onEdit={() => setEditor('state')} t={t} tone={core.paused ? 'warning' : ''} />
            <SettingCard icon="fa-user-shield" title={t('marketOwner')} value={shortAddress(core.owner)} description={t('ownershipCardHint')} detail={<AddressValue value={core.owner} copyValue={market.copyValue} label={t('copy')} />} onEdit={() => setEditor('ownership')} t={t} />
            <SettingCard icon="fa-truck-loading" title={t('migration')} value={core.migrationActive ? t('migrationActive') : t('notStarted')} description={t('migrationCardHint')} detail={core.migrationActive ? <AddressValue value={core.migrationTarget} copyValue={market.copyValue} label={t('copy')} /> : <span>{t('migrationReady')}</span>} onEdit={() => setEditor('migration')} t={t} tone={core.migrationActive ? 'warning' : ''} />
        </div>
        {Editor && <Editor market={market} t={t} onClose={() => setEditor('')} />}
    </div>
}
